/**
 * Filter Dropdown Component
 *
 * Reusable filter dropdown for vulnerability tables.
 * Provides filtering by severity, status, and plugin.
 */

import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Filter } from "lucide-react";
import type { SeverityLevel, LLMScanResultStatus } from "@/types/llm";
import { formatPluginName } from "@/utils/llmFormat";

interface FilterDropdownProps {
  severityFilter: SeverityLevel | undefined;
  statusFilter: LLMScanResultStatus | undefined;
  pluginFilter: string | undefined;
  uniquePlugins: string[];
  onSeverityFilter: (severity: SeverityLevel | "all") => void;
  onStatusFilter: (status: LLMScanResultStatus | "all") => void;
  onPluginFilter: (pluginId: string | "all") => void;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  severityFilter,
  statusFilter,
  pluginFilter,
  uniquePlugins,
  onSeverityFilter,
  onStatusFilter,
  onPluginFilter,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="text-sm">
          <Filter className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">Filters</span>
          <span className="sm:hidden">Filter</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Severity</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={!severityFilter}
          onCheckedChange={() => onSeverityFilter("all")}
        >
          All
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={severityFilter === "critical"}
          onCheckedChange={() => onSeverityFilter("critical")}
        >
          Critical
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={severityFilter === "high"}
          onCheckedChange={() => onSeverityFilter("high")}
        >
          High
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={severityFilter === "medium"}
          onCheckedChange={() => onSeverityFilter("medium")}
        >
          Medium
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={severityFilter === "low"}
          onCheckedChange={() => onSeverityFilter("low")}
        >
          Low
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Status</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={!statusFilter}
          onCheckedChange={() => onStatusFilter("all")}
        >
          All
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={statusFilter === "PASS"}
          onCheckedChange={() => onStatusFilter("PASS")}
        >
          Pass
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={statusFilter === "FAIL"}
          onCheckedChange={() => onStatusFilter("FAIL")}
        >
          Fail
        </DropdownMenuCheckboxItem>
        {uniquePlugins.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Plugin</DropdownMenuLabel>
            <DropdownMenuCheckboxItem
              checked={!pluginFilter}
              onCheckedChange={() => onPluginFilter("all")}
            >
              All
            </DropdownMenuCheckboxItem>
            {uniquePlugins.map((plugin) => (
              <DropdownMenuCheckboxItem
                key={plugin}
                checked={pluginFilter === plugin}
                onCheckedChange={() => onPluginFilter(plugin)}
              >
                {formatPluginName(plugin)}
              </DropdownMenuCheckboxItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
